import React, { useState, useMemo } from 'react';
import { Plus, Calendar, User, Trash2, Filter } from 'lucide-react';
import Card, { CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import SampleModal from '../components/modals/SampleModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import { useAppStore } from '../store';
import { Sample } from '../types';
import { format, addDays, isAfter, isBefore, parseISO } from 'date-fns';
import Select from '../components/ui/Select';
import { useAuth } from '../context/AuthContext';

const Samples: React.FC = () => {
  const { samples, clients, salespersons, addSample, updateSample, deleteSample } = useAppStore();
  const { isAdmin } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sampleToDelete, setSampleToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);



  // State for column clear confirmation
  const [clearColumnConfirmOpen, setClearColumnConfirmOpen] = useState(false);
  const [columnToClear, setColumnToClear] = useState<string | null>(null);

  // Filters
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [salespersonFilter, setSalespersonFilter] = useState<string>('all');
  const [deliveryDateFilter, setDeliveryDateFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const handleDragStart = (e: React.DragEvent, sample: Sample) => {
    e.dataTransfer.setData('sampleId', sample.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, status: Sample['status']) => {
    e.preventDefault();
    const sampleId = e.dataTransfer.getData('sampleId');
    
    try {
      await updateSample(sampleId, { status });
    } catch (error) {
      console.error('Error updating sample status:', error);
      setError('Error al actualizar el estado de la muestra');
      setShowErrorModal(true);
    }
  };

  // --- helper ---------------------------
const clearSampleDraft = () => {
  localStorage.removeItem('sample_form_data');
  localStorage.removeItem('sample_approved_file');
  localStorage.removeItem('sample_dtf_file');
  localStorage.removeItem('sample_embroidery_file');
  localStorage.removeItem('sample_payment_receipt');
  localStorage.removeItem('sample_shipping_label');
  localStorage.removeItem('sample_file_names');
};

  // Apply filters to samples
  const filteredSamples = useMemo(() => {
    return samples.filter(sample => {
      // Filter by client
      if (clientFilter !== 'all' && sample.client_id !== clientFilter) {
        return false;
      }

      // Filter by salesperson
      if (salespersonFilter !== 'all' && sample.salesperson !== salespersonFilter) {
        return false;
      }

      // Filter by delivery date
      if (deliveryDateFilter !== 'all') {
        const today = new Date();
        const deliveryDate = parseISO(sample.delivery_date);
        
        switch (deliveryDateFilter) {
          case '2days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 2));
          case '7days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 7));
          case '15days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 15));
          default:
            return true;
        }
      }

      return true;
    });
  }, [samples, clientFilter, salespersonFilter, deliveryDateFilter]);

  const getColumnSamples = (status: Sample['status']) => 
    filteredSamples.filter(sample => sample.status === status);

  const handleEditSample = (sample: Sample) => {
    clearSampleDraft();
    setSelectedSample(sample);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSampleToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sampleToDelete) return;

    try {
      await deleteSample(sampleToDelete);
    } catch (error) {
      console.error('Error deleting sample:', error);
      setError('Error al eliminar la muestra');
      setShowErrorModal(true);
    }
  };

  const handleSaveSample = async (sample: Omit<Sample, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (selectedSample) {
        await updateSample(selectedSample.id, sample);
      } else {
        await addSample(sample);
      }
      setIsModalOpen(false);
      setSelectedSample(undefined);
    } catch (error) {
      console.error('Error saving sample:', error);
      setError('Error al guardar la muestra');
      setShowErrorModal(true);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setClientFilter('all');
    setSalespersonFilter('all');
    setDeliveryDateFilter('all');
  };

  // Handle clear column
  const handleClearColumn = (status: string) => {
    setColumnToClear(status);
    setClearColumnConfirmOpen(true);
  };

  // Confirm clear column
  const confirmClearColumn = async () => {
    if (!columnToClear) return;
    
    try {
      // Get all sample IDs for the selected status
      const sampleIds = filteredSamples
        .filter(sample => sample.status === columnToClear)
        .map(sample => sample.id);
      
      // Delete each sample
      for (const id of sampleIds) {
        await deleteSample(id);
      }
      
      setClearColumnConfirmOpen(false);
      setColumnToClear(null);
    } catch (error: any) {
      console.error('Error clearing column:', error);
      setError(error.message || 'Error al eliminar las muestras');
      setShowErrorModal(true);
    }
  };

  const renderSample = (sample: Sample) => {
    const client = clients.find(c => c.id === sample.client_id);
    
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleEditSample(sample);
    };
    
    return (
      <div
        key={sample.id}
        draggable
        onDragStart={(e) => handleDragStart(e, sample)}
        onClick={handleClick}
        className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all duration-200 relative group select-none border border-transparent hover:border-blue-200 dark:hover:border-blue-700"
        title="Click para editar o arrastrar para mover"
      >
        {/* Delete button */}
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent opening edit modal
            handleDeleteClick(sample.id);
          }}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="h-4 w-4" />
        </button>

        <h4 className="font-medium text-gray-900 dark:text-white">{sample.name}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{client?.name || 'Cliente no encontrado'}</p>
        <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
          <Calendar className="h-4 w-4 mr-1" />
          {format(new Date(sample.delivery_date), 'dd/MM/yyyy')}
        </div>
        <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
          <User className="h-4 w-4 mr-1" />
          {sample.salesperson}
        </div>
        {sample.comment && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{sample.comment}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Muestras</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={<Filter className="h-4 w-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={() => {
              clearSampleDraft();
              setSelectedSample(undefined);
              setIsModalOpen(true);
            }}
          >
            Nueva Muestra
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <div className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-40">
                <Select
                  label="Cliente"
                  value={clientFilter}
                  onChange={setClientFilter}
                  options={[
                    { value: 'all', label: 'Todos' },
                    ...clients.map(client => ({
                      value: client.id,
                      label: client.name
                    }))
                  ]}
                />
              </div>
              
              <div className="w-40">
                <Select
                  label="Vendedor"
                  value={salespersonFilter}
                  onChange={setSalespersonFilter}
                  options={[
                    { value: 'all', label: 'Todos' },
                    ...salespersons.map(sp => ({
                      value: sp.name,
                      label: sp.name
                    }))
                  ]}
                />
              </div>
              
              <div className="w-40">
                <Select
                  label="Entrega"
                  value={deliveryDateFilter}
                  onChange={setDeliveryDateFilter}
                  options={[
                    { value: 'all', label: 'Todas' },
                    { value: '2days', label: 'Próx. 2 días' },
                    { value: '7days', label: 'Próx. 7 días' },
                    { value: '15days', label: 'Próx. 15 días' }
                  ]}
                />
              </div>
              
              <div className="flex items-center">
                <Button
                  variant="outline"
                  icon={<Filter className="h-4 w-4" />}
                  onClick={resetFilters}
                  className="h-12 px-3"
                >
                  Limpiar Filtros
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Tablero de Muestras" />
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Pending Column */}
            <div 
              className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'pending')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Pendiente <span className="font-normal text-sm">({getColumnSamples('pending').length})</span>
                </h3>
                {isAdmin && getColumnSamples('pending').length > 0 && (
                  <button
                    onClick={() => handleClearColumn('pending')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las muestras pendientes"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {getColumnSamples('pending').map(renderSample)}
                {getColumnSamples('pending').length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay muestras pendientes
                  </div>
                )}
              </div>
            </div>

            {/* In Progress Column */}
            <div 
              className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'in_progress')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  En Proceso <span className="font-normal text-sm">({getColumnSamples('in_progress').length})</span>
                </h3>
                {isAdmin && getColumnSamples('in_progress').length > 0 && (
                  <button
                    onClick={() => handleClearColumn('in_progress')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las muestras en proceso"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {getColumnSamples('in_progress').map(renderSample)}
                {getColumnSamples('in_progress').length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay muestras en proceso
                  </div>
                )}
              </div>
            </div>

            {/* Finished Column */}
            <div 
              className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'finished')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Terminado <span className="font-normal text-sm">({getColumnSamples('finished').length})</span>
                </h3>
                {isAdmin && getColumnSamples('finished').length > 0 && (
                  <button
                    onClick={() => handleClearColumn('finished')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las muestras terminadas"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {getColumnSamples('finished').map(renderSample)}
                {getColumnSamples('finished').length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay muestras terminadas
                  </div>
                )}
              </div>
            </div>

            {/* Shipped Column */}
            <div 
              className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'shipped')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Despachado <span className="font-normal text-sm">({getColumnSamples('shipped').length})</span>
                </h3>
                {isAdmin && getColumnSamples('shipped').length > 0 && (
                  <button
                    onClick={() => handleClearColumn('shipped')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las muestras despachadas"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {getColumnSamples('shipped').map(renderSample)}
                {getColumnSamples('shipped').length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay muestras despachadas
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <SampleModal
        key={selectedSample ? selectedSample.id : 'new'}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSample(undefined);
        }}
        onSave={handleSaveSample}
        initialData={selectedSample}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setSampleToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Muestra"
        message="¿Estás seguro de que deseas eliminar esta muestra? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmModal
        isOpen={clearColumnConfirmOpen}
        onClose={() => {
          setClearColumnConfirmOpen(false);
          setColumnToClear(null);
        }}
        onConfirm={confirmClearColumn}
        title={`Eliminar todas las muestras ${
          columnToClear === 'pending' ? 'pendientes' :
          columnToClear === 'in_progress' ? 'en proceso' :
          columnToClear === 'finished' ? 'terminadas' : 'despachadas'
        }`}
        message={`¿Estás seguro de que deseas eliminar todas las muestras ${
          columnToClear === 'pending' ? 'pendientes' :
          columnToClear === 'in_progress' ? 'en proceso' :
          columnToClear === 'finished' ? 'terminadas' : 'despachadas'
        }? Esta acción no se puede deshacer.`}
        confirmText="Eliminar todas"
        variant="danger"
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </div>
  );
};

export default Samples;